// dependencies
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { View, TouchableWithoutFeedback } from "react-native";
import { useIntl } from "react-intl";
import { StyleSheet } from "aphrodite";

// components
import SurroundActivityIndicator from "@cloudspire/legacy-resources/src/components/ActivityIndicator/Surround";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Button from "@cloudspire/legacy-resources/src/components/Button2";
import BookingDatePickerLegend from "../../components/Booking/DatePicker/Legend";
import {
  IconCaretLeftO,
  IconCaretRightO,
} from "@cloudspire/legacy-svg-icon-library";

// containers
import { DatePickerYear } from "@cloudspire/legacy-resources/src/components/DatePicker";
import DatePickerDay from "./DatePickerDay";
import { DatePickerProvider } from "@cloudspire/legacy-resources/src/containers/HeadlessDatePicker/datePicker";

// contexts
import { useRequestContext } from "@cloudspire/legacy-resources/src/contexts/requestContext";
import { AvailabilitiesProvider } from "./availabilities";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

// libraries
import { emToPx } from "@cloudspire/legacy-resources/src/libraries";
import { noop, get } from "@cloudspire/legacy-shared/src/libraries";
import EnhancedDate, {
  min as enhancedDateMin,
} from "@cloudspire/legacy-resources/src/libraries/EnhancedDate";
import { fromString } from "../../libraries/EnhancedDate";

// helpers
import bookingDatePickerGetDay from "@cloudspire/legacy-resources/src/helpers/booking/datePicker/getDay";
import { availabilityIsAvailable } from "../../helpers/availability";

// api
import { roomAvailabilitiesFindByReference } from "@cloudspire/legacy-resources/src/api/room/availabilities";

const styles = StyleSheet.create({
  apply: {
    paddingTop: "0.3125rem",
    paddingBottom: "0.3125rem",
  },
  legend: {
    marginTop: "1rem",
  },
});

type PropsType = {
  date: EnhancedDate;
  channelId: string;
  roomReference: string;
  start: EnhancedDate;
  end: EnhancedDate;
  min?: EnhancedDate;
  readOnly?: boolean;
  mode?: "checkin" | "checkout";
  shouldRenderApply?: boolean;
  shouldRenderLegend?: boolean;
  onChangeDate?: (newDate: EnhancedDate) => void;
  onSelect?: ({ start, end }) => void;
  onPressApply?;
};

/**
 * Récupère la date maximale possible pour la sélection de la date de sortie.
 * @param {object} param0
 * @param {object} param0.availabilitiesData
 * @param {EnhancedDate} param0.start
 * @param {EnhancedDate} param0.to
 * @return {EnhancedDate}
 */
function getMaxCheckout({ availabilitiesData, start, to }) {
  if (availabilitiesData) {
    for (const date = start.clone(); date.isLessThan(to); date.addDate(1)) {
      const dateKey = date.format("Y-m-d");
      const availability = availabilitiesData[dateKey];

      if (!availabilityIsAvailable({ availability })) {
        return date;
      }
    }
  }

  return to;
}

const BookingDatePicker: React.FunctionComponent<PropsType> = (props) => {
  const {
    date,
    start,
    end,
    min = null,
    readOnly = false,
    channelId,
    roomReference,
    shouldRenderApply = false,
    shouldRenderLegend = true,
    mode: tempMode,
    onChangeDate = noop,
    onSelect = noop,
    onPressApply = noop,
  } = props;

  const intl = useIntl();

  const requestContext = useRequestContext();

  const { matches } = useViewportMedia({
    queries: [
      {
        minWidth: 800,
      },
    ],
  });

  const from = useMemo(() => date.clone().startOfDays().setDate(1), [date]);

  const to = useMemo(
    () =>
      from
        .clone()
        .addMonth(matches ? 1 : 0)
        .setDateToLastOfMonth(),
    [from, matches]
  );

  const [hover, setHover] = useState(null);

  const handleChangeSelect = useCallback(
    (selectedDate: EnhancedDate) => {
      let newStart = start;
      let newEnd = end;

      if (null === start) {
        newStart = selectedDate;
      } else if (null === end) {
        newEnd = selectedDate;
      } else {
        newStart = selectedDate;
        newEnd = null;
      }

      onSelect({ start: newStart, end: newEnd });
    },
    [start, end, onSelect]
  );

  const handleChangeHover = setHover;

  const [availabilitiesResponse, setAvailabilitiesResponse] = useState({
    data: null,
    error: null,
    loading: false,
  });

  const mode = tempMode
    ? tempMode
    : null === start || (start && end)
    ? "checkin"
    : "checkout";

  // TODO: replace by SWR
  useEffect(() => {
    setAvailabilitiesResponse((availabilitiesResponse) => ({
      ...availabilitiesResponse,
      loading: true,
    }));

    roomAvailabilitiesFindByReference({
      parameters: {
        reference: roomReference,
      },
      query: {
        channelId,
        from: enhancedDateMin(from, start).clone().addDate(-1).format("Y-m-d"),
        to: to.format("Y-m-d"),
        checkin:
          "checkin" === mode && null !== start
            ? start.format("Y-m-d")
            : undefined,
      },
      requestContext,
    })
      .then((response) => {
        setAvailabilitiesResponse({
          data: get(response, "body.data"),
          error: null,
          loading: false,
        });
      })
      .catch((error) => {
        console.warn(error);

        setAvailabilitiesResponse({
          data: null,
          error,
          loading: false,
        });
      });
  }, [channelId, roomReference, start, from, to, requestContext, mode]);

  const max = start
    ? getMaxCheckout({
        availabilitiesData: availabilitiesResponse.data,
        start,
        to,
      })
    : null;

  const dayStates = useMemo(() => {
    const dayStates = {};

    if (availabilitiesResponse.data) {
      for (const [dateKey] of Object.entries(availabilitiesResponse.data)) {
        const date = fromString(dateKey);

        dayStates[dateKey] = bookingDatePickerGetDay({
          availabilitiesData: availabilitiesResponse.data,
          mode,
          date,
          start,
          end,
          hover,
          max,
        });
      }
    }

    return dayStates;
  }, [availabilitiesResponse.data, mode, start, end, hover, max]);

  return (
    <AvailabilitiesProvider value={{ dayStates }}>
      <DatePickerProvider
        value={{
          DatePickerDay,
          start,
          end,
          hover,
          min,
          readOnly,
          onChangeSelect: handleChangeSelect,
          onChangeHover: handleChangeHover,
        }}
      >
        <SurroundActivityIndicator loading={availabilitiesResponse.loading}>
          <View
            style={{
              padding: emToPx(1),
              borderStyle: "solid",
              borderColor: "#eee",
              backgroundColor: "#ffffff",
              borderWidth: 1,
              borderRadius: 3,
            }}
          >
            <View style={{ position: "relative" }}>
              <DatePickerYear from={from} to={to} />

              <TouchableWithoutFeedback
                hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
                onPress={() => onChangeDate(from.clone().addMonth(-1))}
              >
                <View
                  style={{
                    position: "absolute",
                    top: 16,
                    left: 5,
                    cursor: "pointer",
                  }}
                >
                  <IconCaretLeftO />
                </View>
              </TouchableWithoutFeedback>

              <TouchableWithoutFeedback
                hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
                onPress={() => onChangeDate(from.clone().addMonth(1))}
              >
                <View
                  style={{
                    position: "absolute",
                    top: 16,
                    right: 5,
                    cursor: "pointer",
                  }}
                >
                  <IconCaretRightO />
                </View>
              </TouchableWithoutFeedback>

              {shouldRenderApply && (
                <>
                  <Stack marginTop={1} />

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div>
                      <Button
                        label={intl.formatMessage({
                          defaultMessage: "Appliquer",
                        })}
                        appearance="secondary"
                        aStyle={styles.apply}
                        onPress={onPressApply}
                      />
                    </div>
                  </div>
                </>
              )}

              {shouldRenderLegend && (
                <View style={{ marginTop: "1rem", maxWidth: "29rem" }}>
                  <BookingDatePickerLegend />
                </View>
              )}
            </View>
          </View>
        </SurroundActivityIndicator>
      </DatePickerProvider>
    </AvailabilitiesProvider>
  );
};

export default BookingDatePicker;
